import { entryHidden } from '@/lib/aiEntries';

import { api } from './client';

/**
 * Задания куратора: что показывать ученикам в списке дел и с какой
 * периодичностью.
 */
export type TaskRepeatKind = 'daily' | 'weekdays' | 'once';
export type TaskAudienceType = 'group' | 'marathon' | 'student';

export type TaskRule = {
  id: string;
  title: string;
  kind: 'section' | 'lesson' | 'text';
  target: string;
  audience_type: TaskAudienceType;
  marathon_id?: string | null;
  group_id?: string | null;
  student_id?: string | null;
  repeat_kind: TaskRepeatKind;
  /** 1 — понедельник … 7 — воскресенье. */
  weekdays: number[];
  starts_on?: string | null;
  ends_on?: string | null;
  is_active: boolean;
};

export const curatorTasksApi = {
  list: () => api.get<{ items: TaskRule[] }>('/curator/daily-tasks').then((r) => r.data?.items ?? []),
  create: (payload: Partial<TaskRule>) => api.post<TaskRule>('/curator/daily-tasks', payload).then((r) => r.data),
  update: (id: string, payload: Partial<TaskRule>) =>
    api.put<TaskRule>(`/curator/daily-tasks/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/curator/daily-tasks/${id}`).then((r) => r.data),
};

/**
 * Разделы приложения, которые куратор может задать одним нажатием.
 *
 * ⚠️ СПИСОК, А НЕ ПОЛЕ ДЛЯ АДРЕСА. Куратор не должен угадывать внутренние пути:
 * опечатка дала бы задание, ведущее в никуда, и заметил бы это ученик.
 */
export const TASK_SECTIONS: { target: string; labelKey: string }[] = [
  // 🔴 БЫЛО `/app/vocab` — такого маршрута НЕТ, он называется `/app/vocabulary`.
  // Куратор ставил заданию раздел «Словарь», ученик нажимал и упирался в 404.
  // Опечатку в списке видит только тот, кто по ней пройдёт: ниже стоит сторож,
  // который сверяет ВСЕ эти пути с маршрутами приложения.
  { target: '/app/vocabulary', labelKey: 'curatorTasks.sectionVocab' },
  { target: '/app/ielts/listening', labelKey: 'curatorTasks.sectionListening' },
  { target: '/app/ielts/reading', labelKey: 'curatorTasks.sectionReading' },
  { target: '/app/ielts/speaking', labelKey: 'curatorTasks.sectionSpeaking' },
  { target: '/app/ielts/writing', labelKey: 'curatorTasks.sectionWriting' },
  { target: '/app/homework', labelKey: 'curatorTasks.sectionHomework' },
  { target: '/app/cue-cards', labelKey: 'curatorTasks.sectionCueCards' },
];

/**
 * Разделы для выбора у куратора — без закрытых входов (AI speaking, D22).
 *
 * ⚠️ Раздел уже сохранённого задания остаётся в списке, даже если он закрыт:
 * иначе при правке задания поле молча стало бы пустым, и куратор стёр бы раздел,
 * сам того не заметив. Ученик по такому заданию попадёт в тренажёры с
 * объяснением, где теперь Speaking.
 *
 * Полный `TASK_SECTIONS` не урезается: по нему сторож сверяет адреса с маршрутами.
 */
export function taskSectionsForPicker(current?: string | null): { target: string; labelKey: string }[] {
  return TASK_SECTIONS.filter((s) => s.target === current || !entryHidden(s.target));
}

/**
 * Короткая подпись периодичности — то, что куратор увидит в списке своих
 * заданий, не открывая карточку.
 */
export function repeatSummary(
  rule: Pick<TaskRule, 'repeat_kind' | 'weekdays'>,
  names: string[],
  everyDay: string,
  once: string,
): string {
  if (rule.repeat_kind === 'daily') return everyDay;
  if (rule.repeat_kind === 'once') return once;
  const days = (rule.weekdays ?? [])
    .filter((d) => d >= 1 && d <= 7)
    .sort((a, b) => a - b)
    .map((d) => names[d - 1]);
  return days.join(', ');
}
